import {
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import { Stack } from "@mui/system";
import { LockLocked20Px } from "@qubit/autoparts";
import { useToast } from "@qubit/autoparts/src/toast/useToast";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { LabelWithInfoTooltip } from "~/features/preview/components/LabelWithInfoTooltip";
import { SettingsInfoBlock } from "~/features/settings/SettingsInfoBlock";
import { useDebounce } from "~/hooks/useDebounce";
import {
  numberOfCompartmentsToBinTypeMap,
  type NumberOfCompartmentsType
} from "~/lib/bin";
import { percentRegex, runAsyncSafely } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectUserIsAdmin,
  selectUsersClientId
} from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  useGetBinConfigurationsQuery,
  usePatchBinConfigurationMutation
} from "~/redux/warehouse/bin.hooks";

const FILL_RATE_SAVE_DEBOUNCE_MS = 1000;

type FillRateInputProps = {
  configurationId: string;
  maxBinFillPercent: number;
  editable: boolean;
};

function FillRateInput({
  configurationId,
  maxBinFillPercent,
  editable
}: FillRateInputProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { successToast, errorToast } = useToast();
  const [value, setValue] = useState(String(maxBinFillPercent));
  const [hasError, setHasError] = useState(false);
  const [patch, { isLoading: isPatching }] = usePatchBinConfigurationMutation();
  const debouncedValue = useDebounce(value, FILL_RATE_SAVE_DEBOUNCE_MS, value);
  const saveInFlightRef = useRef(false);
  const persistedFillPercentRef = useRef(maxBinFillPercent);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setValue(String(maxBinFillPercent));
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
    setHasError(false);
    persistedFillPercentRef.current = maxBinFillPercent;
  }, [maxBinFillPercent]);

  useEffect(() => {
    void (async () => {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (!editable || saveInFlightRef.current) {
        return;
      }

      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      if (debouncedValue !== value) {
        return;
      }

      const isValidNumber =
        debouncedValue.length > 0 && percentRegex.test(debouncedValue);
      if (!isValidNumber) {
        if (debouncedValue !== String(persistedFillPercentRef.current)) {
          setHasError(true);
        }
        return;
      }

      if (+debouncedValue === persistedFillPercentRef.current) {
        setHasError(false);
        return;
      }

      saveInFlightRef.current = true;

      try {
        await runAsyncSafely(
          () =>
            patch({
              configurationId,
              maxBinFillPercent: +debouncedValue
            }).unwrap(),
          {
            onSuccess: () => {
              persistedFillPercentRef.current = +debouncedValue;
              setHasError(false);
              successToast(t("fill rate update success"));
            },
            onError: (err) => {
              errorToast(getMessageFromRtkError(err));
            }
          }
        );
      } finally {
        saveInFlightRef.current = false;
      }
    })();
  }, [
    configurationId,
    debouncedValue,
    editable,
    errorToast,
    maxBinFillPercent,
    patch,
    successToast,
    t,
    value
  ]);

  return (
    <TextField
      disabled={!editable || isPatching}
      size="medium"
      value={value}
      onChange={
        editable
          ? (event) => {
              setValue(event.target.value);
              setHasError(false);
            }
          : undefined
      }
      error={hasError}
      helperText={hasError ? t("fill rate validation error") : null}
      sx={{
        width: "100px",
        ...(!editable
          ? {
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: theme.palette.text.primary,
                color: theme.palette.text.primary,
                opacity: 1
              }
            }
          : {})
      }}
      slotProps={{
        input: {
          startAdornment: !editable ? (
            <InputAdornment position="start">
              <LockLocked20Px fill={theme.palette.text.secondary} aria-hidden />
            </InputAdornment>
          ) : undefined,
          endAdornment: <InputAdornment position="end">%</InputAdornment>
        }
      }}
    />
  );
}

export function CubingFillRates() {
  const { t } = useTranslation();
  const isAdmin = useAppSelector(selectUserIsAdmin);

  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const usersClientId = useAppSelector(selectUsersClientId);

  const { isLoading: isLoadingBinConfigurations, data: binConfigurations } =
    useGetBinConfigurationsQuery(undefined, {
      skip: !usersClientId || !usersFulfillmentCenter
    });

  if (isLoadingBinConfigurations) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          justifyContent: "center"
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ ml: "134px" }}>
        <LabelWithInfoTooltip
          label={
            <Typography variant="body2" component="span">
              {t("fill rate")}
            </Typography>
          }
          tooltip={t("fill rate info tooltip")}
        />
      </Box>
      <Stack flexDirection="column" gap={3}>
        {binConfigurations?.map(
          ({ configurationId, numberOfCompartments, maxBinFillPercent }) => (
            <SettingsInfoBlock
              key={configurationId}
              mainText={t(
                numberOfCompartmentsToBinTypeMap[
                  numberOfCompartments as NumberOfCompartmentsType
                ]
              )}
              dataElements={[
                {
                  element: (
                    <FillRateInput
                      configurationId={configurationId}
                      maxBinFillPercent={maxBinFillPercent}
                      editable={Boolean(isAdmin)}
                    />
                  )
                }
              ]}
            />
          )
        )}
      </Stack>
    </>
  );
}
